import { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  UserCog, Plus, Power, PowerOff, X, RotateCcw, KeyRound, Trash2, Calendar,
} from 'lucide-react';
import { ROLES, ROLE_LABELS } from '../lib/permissions';
import { formatCaracasDateTime } from '../lib/timezone';

const STATUS_META = {
  ACTIVO:    { label: 'Activo',     cls: 'stage-cerrado_ganado' },
  INACTIVO:  { label: 'Inactivo',   cls: 'stage-cerrado_perdido' },
  PENDIENTE: { label: 'Pendiente',  cls: 'stage-contactado' },
  BLOQUEADO: { label: 'Bloqueado',  cls: 'stage-cerrado_perdido' },
};

const DAY_LABELS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

// ──────────────────────────────────────────────────────────────────
// Create user — password is now OPTIONAL. Empty password → PENDIENTE.
// ──────────────────────────────────────────────────────────────────

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'asesor' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Send WITHOUT password → server marks user as PENDIENTE.
      const { data } = await api.post('/admin/users', {
        email: form.email,
        name: form.name,
        role: form.role,
      });
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo crear el usuario');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="create-user-modal" className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><UserCog size={20} /> Crear Usuario</h2>
          <button className="close-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo *</label>
            <input data-testid="new-user-name" value={form.name} onChange={update('name')} required minLength={2} />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input data-testid="new-user-email" type="email" value={form.email} onChange={update('email')} required />
          </div>
          <div className="form-group">
            <label>Rol *</label>
            <select data-testid="new-user-role" value={form.role} onChange={update('role')} required>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          <div className="form-info" data-testid="pending-info">
            El usuario será creado en estado <strong>PENDIENTE</strong>. La primera contraseña que ingrese al iniciar sesión quedará registrada como su clave oficial.
          </div>
          {error && <div data-testid="new-user-error" className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
            <button data-testid="submit-new-user" type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Weekly shifts modal — replace-all idempotent PUT.
// ──────────────────────────────────────────────────────────────────

function ShiftsModal({ user, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/shifts`);
        if (!alive) return;
        setRows(data.shifts || []);
      } catch (err) {
        setError(err?.response?.data?.detail || 'No se pudieron cargar las jornadas');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user.id]);

  const toggleDay = (dow) => {
    setRows(prev => {
      const has = prev.find(r => r.day_of_week === dow);
      if (has) return prev.filter(r => r.day_of_week !== dow);
      return [...prev, { day_of_week: dow, start_time: '08:00', end_time: '17:00', active: true }];
    });
  };
  const updateField = (dow, field, value) => {
    setRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/admin/users/${user.id}/shifts`, { shifts: rows });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo guardar la jornada');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="shifts-modal" className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2><Calendar size={20} /> Jornada de {user.name}</h2>
          <button className="close-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-muted" style={{ marginTop: 0, fontSize: 12 }}>
          Horario semanal fijo en hora Caracas (UTC-4). El asesor puede ingresar hasta 10 minutos antes de la hora de inicio. La sesión se cierra automáticamente al fin de la jornada.
        </p>
        {loading ? (
          <div className="text-muted">Cargando…</div>
        ) : (
          <div className="shifts-grid">
            {DAY_LABELS.map((label, dow) => {
              const r = rows.find(x => x.day_of_week === dow);
              const enabled = Boolean(r);
              return (
                <div key={dow} className={`shift-row ${enabled ? 'shift-row-on' : 'shift-row-off'}`}>
                  <label className="shift-day">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => toggleDay(dow)}
                      data-testid={`shift-day-${dow}`}
                    />
                    {label}
                  </label>
                  {enabled && (
                    <div className="shift-times">
                      <input
                        type="time"
                        value={r.start_time}
                        data-testid={`shift-start-${dow}`}
                        onChange={(e) => updateField(dow, 'start_time', e.target.value)}
                      />
                      <span>—</span>
                      <input
                        type="time"
                        value={r.end_time}
                        data-testid={`shift-end-${dow}`}
                        onChange={(e) => updateField(dow, 'end_time', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button data-testid="save-shifts" type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar Jornada'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────

export default function UsersManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [shiftsFor, setShiftsFor] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/admin/users');
      const unique = Array.from(new Map(data.map(u => [u.id, u])).values());
      setUsers(unique);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patchUser = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${id}`, patch);
      setUsers(prev => prev.map(u => u.id === id ? data : u));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo actualizar el usuario');
    }
  };
  const reactivateUser = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/reactivate`);
      setUsers(prev => prev.map(u => u.id === id ? data : u));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo reactivar el usuario');
    }
  };
  const resetPasswordUser = async (u) => {
    if (!window.confirm(
      `¿Restablecer la contraseña de ${u.name}?\n\nSe borrará el hash actual y el usuario volverá a estado PENDIENTE. La próxima contraseña que escriba al iniciar sesión será la nueva.`,
    )) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setUsers(prev => prev.map(x => x.id === u.id ? data : x));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo restablecer la contraseña');
    }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(
      `¿ELIMINAR PERMANENTEMENTE a ${u.name} (${u.email})?\n\nEsta acción no se puede deshacer. Los registros de auditoría se conservan, pero el usuario y sus jornadas se borrarán.`,
    )) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo eliminar el usuario');
    }
  };

  if (loading) return <div className="page-loading">Cargando usuarios...</div>;

  return (
    <div data-testid="users-management-page" className="contacts-page">
      <div className="contacts-toolbar">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <UserCog size={18} /> Gestion de Usuarios
        </h3>
        <button data-testid="open-create-user" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Crear Usuario
        </button>
      </div>

      {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="contacts-table-wrapper" style={{ marginTop: 16 }}>
        <table data-testid="users-table" className="contacts-table">
          <thead>
            <tr>
              <th>Nombre</th><th>Email</th><th>Rol</th><th>Estado</th>
              <th>Ultimo ingreso</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const status = u.user_status || (u.is_active ? 'ACTIVO' : 'INACTIVO');
              const st = STATUS_META[status] || STATUS_META.INACTIVO;
              const isInactive = status !== 'ACTIVO';
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="td-name">{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      data-testid={`role-select-${u.id}`}
                      value={u.role}
                      onChange={(e) => patchUser(u.id, { role: e.target.value })}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </td>
                  <td>
                    <span data-testid={`status-badge-${u.id}`} className={`stage-badge ${st.cls}`}>
                      {st.label}
                    </span>
                  </td>
                  <td>{u.last_login_at ? formatCaracasDateTime(u.last_login_at) : '—'}</td>
                  <td className="user-actions">
                    {isInactive && status !== 'PENDIENTE' && (
                      <button
                        data-testid={`reactivate-${u.id}`}
                        className="btn-icon"
                        title="Reactivar (poner en ACTIVO)"
                        onClick={() => reactivateUser(u.id)}
                      >
                        <RotateCcw size={16} color="#059669" />
                      </button>
                    )}
                    <button
                      data-testid={`shifts-${u.id}`}
                      className="btn-icon"
                      title="Configurar jornada laboral"
                      onClick={() => setShiftsFor(u)}
                    >
                      <Calendar size={16} color="#2563eb" />
                    </button>
                    <button
                      data-testid={`reset-password-${u.id}`}
                      className="btn-icon"
                      title="Restablecer contraseña → PENDIENTE"
                      onClick={() => resetPasswordUser(u)}
                    >
                      <KeyRound size={16} color="#d97706" />
                    </button>
                    <button
                      data-testid={`toggle-active-${u.id}`}
                      className="btn-icon"
                      title={u.is_active ? 'Desactivar' : 'Activar'}
                      onClick={() => patchUser(u.id, { is_active: !u.is_active })}
                    >
                      {u.is_active ? <Power size={16} color="#059669" /> : <PowerOff size={16} color="#dc2626" />}
                    </button>
                    <button
                      data-testid={`delete-${u.id}`}
                      className="btn-icon"
                      title="Eliminar permanentemente"
                      onClick={() => deleteUser(u)}
                    >
                      <Trash2 size={16} color="#dc2626" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(u) => setUsers(prev => [...prev, u])}
        />
      )}
      {shiftsFor && (
        <ShiftsModal user={shiftsFor} onClose={() => setShiftsFor(null)} />
      )}
    </div>
  );
}
